import { ReactElement } from 'react';
import { Controller, Control, FieldPath, FieldValues } from 'react-hook-form';
import { ChipInput } from 'components/common/ChipInput/ChipInput';
import { FieldWrapper, FieldWrapperPassThroughProps } from './FieldWrapper';

export type ChipInputFieldProps<T extends FieldValues> = FieldWrapperPassThroughProps & {
  name: FieldPath<T>;
  control: Control<T>;
  placeholder?: string;
  /** Maximum height before scrolling (in pixels or chakra units) */
  maxHeight?: string | number;
  /** Whether to allow duplicate chips */
  allowDuplicates?: boolean;
  width?: string | number;
  minWidth?: string | number;
};

/**
 * ChipInputField component - A form field wrapper for ChipInput using react-hook-form Controller
 * Easy to use with react-hook-form forms, handles validation and error states automatically
 *
 * @example
 * <ChipInputField
 *   name="tags"
 *   control={control}
 *   placeholder="Add tags..."
 * />
 */
export const ChipInputField = <T extends FieldValues>({
  name,
  control,
  label,
  error,
  placeholder = 'Add items...',
  maxHeight = '200px',
  allowDuplicates = false,
  width,
  minWidth,
  labelPosition,
}: ChipInputFieldProps<T>): ReactElement => {
  return (
    <FieldWrapper label={label} error={error} labelPosition={labelPosition}>
      <Controller
        name={name}
        control={control}
        render={({ field: { onChange, value } }) => (
          <ChipInput
            placeholder={placeholder}
            values={value || []}
            onChange={onChange}
            maxHeight={maxHeight}
            allowDuplicates={allowDuplicates}
            width={width}
            minWidth={minWidth}
          />
        )}
      />
    </FieldWrapper>
  );
};
